import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import FileUploader from "@/components/FileUploader";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledUploadLinkPath,
  StyledSectionHeading,
} from "@/common/styles";
import Button from "@/common/elements/Button";
import { getOptionsFromRefList, isNonEmptyVariable } from "@/util/common_function";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import api from "@/services/axios_custom";
import API_ENDPOINTS, { PHOTOS_BASE_URL } from "@/util/api";
import FileUpload from "@/common/elements/InputElements/Upload";

const LinkedScannedDocumentsForm = forwardRef(
  ({ document, documentFileType = "", bookingsFieldsEnabled, isSearchMode = false }, ref) => {
    const [form] = Form.useForm();
    // Nothing is mandatory while Transport's Full Search is active (mirrors
    // TransportDetails.jsx's searchAwareRules) - skip required rules here too.
    const searchAwareRules = (rules) => (isSearchMode ? [] : rules);
    const [isImgUploading, setIsImgUploading] = useState(false);
    // form.getFieldValue() reads a snapshot at render time and isn't
    // reactive - form.setFieldsValue() in the populate-on-edit effect below
    // doesn't re-render this component, so FileUploader never picked up the
    // saved image. useWatch subscribes to the field so it does.
    const fullSizePathValue = Form.useWatch("fullSizePath", form);

    // Expose form methods to parent (same pattern as DocsModal)
    useImperativeHandle(ref, () => ({
      submitForm:        () => form.submit(),
      resetForm:         () => form.resetFields(),
      getFormValues:     () => form.getFieldsValue(),
      validateFields:    () => form.validateFields(),
      form,
    }));

    // Populate form when editing an existing document
    useEffect(() => {
      if (!document) return;

      const isPhoto    = documentFileType === "Scanned Photo";
      const docData    = document?.documents?.[0] ?? {};
      const photoData  = document?.photos?.[0]    ?? {};
      // Shared fields (name/date/author/comments/type) are saved onto
      // photos[0] for Scanned Photo records and documents[0] for Linked
      // Documents (see DocumentsDetailsModal's onSaveClick) - read from
      // whichever one actually holds them.
      const sharedData = isPhoto ? photoData : docData;

      form.setFieldsValue({
        documentName:  sharedData.documentName  ?? null,
        documentDate:  isNonEmptyVariable(sharedData.documentDate)
                         ? dayjs(sharedData.documentDate)
                         : null,
        author:        sharedData.author        ?? "",
        comments:      sharedData.comments      ?? "",
        documentType:  sharedData.documentType  ?? null,
        documentPath:  docData.documentPath  ?? "",
        // photo-specific
        description:   photoData.description ?? "",
        fullSizePath:  isPhoto ? (photoData.fullSizePath ?? "") : "",
        thumbnailPath: isPhoto ? (photoData.thumbnailPath ?? "") : "",
      });
    }, [document]);

    // Image upload handler (same as DocsModal)
    const getFinalPath = (path) => {
      if (path?.includes("\\")) {
        const splitPath = path.split("/");
        if (splitPath.length === 4) {
          splitPath.shift();
          splitPath.shift();
        }
        return splitPath.join("\\");
      }
      return path;
    };

    const onUpload = (e) => {
      const data = new FormData();
      data.append("file", e);
      data.append("imageType",   form.getFieldValue("documentType"));
      data.append("description", form.getFieldValue("description"));

      setIsImgUploading(true);
      api
        .post(API_ENDPOINTS?.imageUpload, data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          const body = response?.data?.body;
          form.setFieldValue("fullSizePath",   body?.fullSizePath);
          form.setFieldValue("thumbnailPath",  body?.thumbnailPath);
        })
        .finally(() => setIsImgUploading(false));
    };

    const isLinkedDocs = documentFileType === "Linked Documents";
    const isPhoto      = documentFileType === "Scanned Photo";

    return (
      <Form form={form} disabled={!bookingsFieldsEnabled}>

        {/* ── Document Information ── */}
        <StyledSectionHeading marginTop="0px">
          <span>Document Information</span>
        </StyledSectionHeading>

        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem label="Document Name" name="documentName">
              <Select
                options={[
                  { value: "",     label: ""     },
                  { value: "Stub", label: "Stub" },
                ]}
                placeholder="Document Name"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol span={8}>
            <FormItem label="Document Date" name="documentDate">
              <CommonDatePicker
                showTime={false}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol span={8}>
            <FormItem label="Author" name="author">
              <TextField placeholder="Author" />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol span={24}>
            <FormItem label="Comments" name="comments">
              <TextField inputType="textarea" rows={3} placeholder="Comments" />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>

        {/* ── Photo Information ── */}
        {isPhoto && (
          <>
            <StyledSectionHeading>
              <span>Photo Information</span>
            </StyledSectionHeading>

            <StyledGridRow gutter={16}>
              <StyledGridCol span={8}>
                <FormItem label="Document Type" name="documentType">
                  <Select
                    options={getOptionsFromRefList("Image_Type")}
                    placeholder="Document Type"
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={8}>
                <FormItem
                  label="Description"
                  name="description"
                  rules={searchAwareRules([{ required: true, message: "Description is required" }])}
                >
                  <TextField placeholder="Description" />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={8}>
                <FormItem label="Upload" name="fullSizePath">
                  <FileUploader
                    browseFileType="image"
                    acceptImageFormats="image/png,image/jpeg,image/jpg"
                    allowMultiple={false}
                    onUpload={onUpload}
                    imageUrlFromParent={
                      fullSizePathValue
                        ? PHOTOS_BASE_URL + getFinalPath(fullSizePathValue)
                        : ""
                    }
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
          </>
        )}

        {/* ── Linked Documents ── */}
        {isLinkedDocs && (
          <>
            <StyledSectionHeading>
              <span>Linked Documents</span>
            </StyledSectionHeading>

            <StyledGridRow gutter={16}>
              <StyledGridCol span={8}>
                <FormItem label="Document Type" name="documentType">
                  <Select
                    options={getOptionsFromRefList("Image_Type")}
                    placeholder="Document Type"
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>

            <StyledGridCol span={24}>
                <FormItem
                    label="Document Path"
                    name="documentPath"
                    rules={searchAwareRules([{ required: true, message: "Document Path is required" }])}
                >
                    <TextField
                    placeholder="Document Path"
                    disabled
                    />
                </FormItem>
                <FileUpload
                    payload={{ documentType: form.getFieldValue("documentType") }}
                    onSuccess={(res) => {
                    form.setFieldValue("documentPath", res?.documentPath ?? "");
                    }}
                />
            </StyledGridCol>
            </StyledGridRow>
          </>
        )}
      </Form>
    );
  }
);

export default LinkedScannedDocumentsForm;